/**
 * Email template rendering engine
 * Handles variable substitution and template customization
 */

import { EmailType, EMAIL_TEMPLATE_REGISTRY, EmailTemplateSchema } from './email-templates.js';
import { EmailBranding } from './email.js';

export interface EmailTemplateOverride {
  subject?: string;           // Custom subject line with {{variables}}
  htmlBody?: string;          // Full HTML template override
  textBody?: string;          // Full plain text template override
  sections?: {                // Partial section overrides
    greeting?: string;
    intro?: string;
    footer?: string;
    [key: string]: string | undefined;
  };
  lastModified?: string;      // ISO timestamp
  modifiedBy?: string;        // User ID who last modified
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

/**
 * Render a template by substituting variables
 * Supports {{variableName}} and {{object.property}} syntax
 */
export function renderTemplate(
  template: string,
  variables: Record<string, any>,
  options?: { escapeHtml?: boolean }
): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const trimmedPath = path.trim();

    // Don't replace SECTION placeholders here - they're handled separately
    if (trimmedPath.startsWith('SECTION:')) {
      return match;
    }

    const value = getNestedValue(variables, trimmedPath);

    if (value === undefined || value === null) {
      // For optional fields, just return empty string
      // Log warning only for non-empty/non-whitespace templates
      if (template.trim().length > 0) {
        console.warn(`[Email Renderer] Template variable not found: ${trimmedPath}`);
      }
      return '';
    }

    // Auto-escape HTML in HTML templates if needed
    if (options?.escapeHtml && typeof value === 'string' && !trimmedPath.toLowerCase().includes('html')) {
      return escapeHtml(value);
    }

    return String(value);
  });
}

/**
 * Get nested value from object using dot notation
 * Example: getNestedValue({a: {b: 'value'}}, 'a.b') returns 'value'
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    if (current === undefined || current === null) {
      return undefined;
    }
    return current[key];
  }, obj);
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}

/**
 * Render template with section overrides
 * Replaces {{SECTION:name}} placeholders with custom or default content
 */
function renderTemplateWithSections(
  baseTemplate: string,
  variables: Record<string, any>,
  sections: Record<string, string>
): string {
  let template = baseTemplate;

  // First, replace section placeholders
  template = template.replace(/\{\{SECTION:([^}]+)\}\}/g, (match, sectionName) => {
    const customContent = sections[sectionName.trim()];
    if (customContent !== undefined) {
      // Render the custom section content with variables
      return renderTemplate(customContent, variables);
    }
    // If no custom section, leave the placeholder for default rendering
    return match;
  });

  // Then render all remaining variables
  return renderTemplate(template, variables);
}

/**
 * Get default section values for an email type
 * These are consistent across all email types for easier maintenance
 * Email-specific content is handled in the full templates (defaultHtml/defaultText)
 */
function getDefaultSections(
  emailType: EmailType,
  variables: Record<string, any>
): Record<string, string> {
  // Base sections that are consistent across all email types
  const sections: Record<string, string> = {
    greeting: `Hi ${variables.toName || 'there'},`,
    intro: 'Please see the details below:',
    callToAction: 'Click the button above to complete the details. It only takes a few minutes!',
    footer: variables.footerText || 'This is an automated message from LittleBooker.',
  };

  // Email-specific sections for happeningReminder
  if (emailType === 'happeningReminder') {
    sections.arrivalInstructions = variables.arrivalInfo || 'Please arrive 15 minutes before the scheduled start time.';
    sections.contactDetails = 'If you have any questions, please reply to this email.';
  }

  // Email-specific sections for artistAndShowInvite
  if (emailType === 'artistAndShowInvite') {
    sections.greeting = `Hi ${variables.artistName || 'there'},`;
    sections.intro = `You've been invited to perform on <strong>${variables.happeningTitle || 'an upcoming show'}</strong> at ${variables.happeningPlace || 'our venue'}.`;
    sections.callToAction = 'Both forms should only take a few minutes to complete. Looking forward to your performance!';
    sections.footer = `This email was sent by ${variables.organizationName || 'LittleBooker'}`;
  }

  // Email-specific sections for technicalUpdateNotification
  if (emailType === 'technicalUpdateNotification') {
    sections.intro = `The technical setup for <strong>${variables.happeningTitle || 'an upcoming show'}</strong> has been updated by ${variables.updatedByName || 'an artist'}. Please review the changes.`;
    sections.footer = `This email was sent by ${variables.organizationName || 'LittleBooker'}`;
  }

  // Email-specific sections for postShow
  if (emailType === 'postShow') {
    sections.intro = `Thanks for your set on <strong>${variables.happeningTitle || 'the show'}</strong>. We loved having you over! We'd love to hear feedback on your experience, and we'd also like to give you an opportunity to share your tracklist with our audience. Find the links below to do that.`;
    sections.footer = `This email was sent by ${variables.organizationName || 'LittleBooker'}`;
  }

  // Email-specific sections for archiveNotification
  if (emailType === 'archiveNotification') {
    sections.intro = `Good news — your show <strong>${variables.happeningTitle || ''}</strong> has been archived and is now available to listen back to. Share it around!`;
    sections.footer = `This email was sent by ${variables.organizationName || 'LittleBooker'}`;
  }

  return sections;
}

/**
 * Main function to resolve and render an email template
 * Handles custom templates, section overrides, and fallbacks
 */
export function renderEmailTemplate(
  emailType: EmailType,
  variables: Record<string, any>,
  customTemplate?: EmailTemplateOverride,
  branding?: EmailBranding
): RenderedEmail {
  const schema = EMAIL_TEMPLATE_REGISTRY[emailType];

  if (!schema) {
    throw new Error(`Unknown email template type: ${emailType}`);
  }

  // Merge branding into variables
  const allVariables = {
    ...variables,
    logoHtml: branding?.logoUrl ? `
      <div style="margin-bottom: 20px;">
        <img src="${branding.logoUrl}" alt="Logo" style="max-width: 200px; max-height: 60px; height: auto;" />
      </div>
    ` : '',
    primaryColor: branding?.primaryColor || '#667eea',
    secondaryColor: branding?.secondaryColor || '#764ba2',
    footerText: branding?.footerText || 'This is an automated message from LittleBooker.',
  };

  // Resolve subject
  const subjectTemplate = customTemplate?.subject || schema.defaultSubject;
  const subject = renderTemplate(subjectTemplate, allVariables);

  // Get default sections
  const defaultSections = getDefaultSections(emailType, allVariables);

  // Merge with legacy branding.templates sections (for backwards compatibility)
  const legacySections = branding?.templates?.[emailType];
  const mergedSections = {
    ...defaultSections,
    ...legacySections,
    ...customTemplate?.sections,
  };

  // Resolve HTML body
  let html: string;
  if (customTemplate?.htmlBody) {
    // Full custom HTML override
    html = renderTemplate(customTemplate.htmlBody, allVariables);
  } else {
    // Use default template with section replacements
    html = renderTemplateWithSections(
      schema.defaultHtml,
      allVariables,
      mergedSections
    );
  }

  // Resolve text body
  let text: string;
  if (customTemplate?.textBody) {
    // Full custom text override
    text = renderTemplate(customTemplate.textBody, allVariables);
  } else {
    // Use default template with section replacements
    text = renderTemplateWithSections(
      schema.defaultText,
      allVariables,
      mergedSections
    );
  }

  return { subject, html, text };
}

/**
 * Validate a custom template
 * Returns array of error messages, empty if valid
 */
export function validateTemplate(
  template: EmailTemplateOverride,
  emailType: EmailType
): string[] {
  const errors: string[] = [];
  const schema = EMAIL_TEMPLATE_REGISTRY[emailType];

  if (!schema) {
    return ['Unknown email template type'];
  }

  // Check template size (100KB limit)
  const templateSize = JSON.stringify(template).length;
  if (templateSize > 100 * 1024) {
    errors.push(`Template size (${Math.round(templateSize / 1024)}KB) exceeds 100KB limit`);
  }

  // Check for required variables in custom templates
  const requiredVars = schema.variables.filter(v => v.required).map(v => v.key);

  if (template.htmlBody) {
    for (const varKey of requiredVars) {
      if (!template.htmlBody.includes(varKey)) {
        errors.push(`HTML template missing required variable: ${varKey}`);
      }
    }
  }

  if (template.textBody) {
    for (const varKey of requiredVars) {
      if (!template.textBody.includes(varKey)) {
        errors.push(`Text template missing required variable: ${varKey}`);
      }
    }
  }

  return errors;
}

/**
 * Extract email branding from account settings
 * Returns EmailBranding with optional emailTemplates
 */
export function getEmailBrandingFromAccount(account: {
  settings?: Record<string, unknown> | string;
}): EmailBranding & { emailTemplates?: Record<string, EmailTemplateOverride> } | undefined {
  // Handle case where settings might be a JSON string
  let settings: Record<string, unknown> | undefined;
  if (typeof account.settings === 'string') {
    try {
      settings = JSON.parse(account.settings) as Record<string, unknown>;
    } catch (e) {
      console.warn('[Email Branding] Failed to parse settings as JSON:', e);
      return undefined;
    }
  } else if (account.settings && typeof account.settings === 'object') {
    settings = account.settings;
  } else {
    return undefined;
  }

  const emailBranding = settings.emailBranding;
  const emailTemplates = settings.emailTemplates;

  if (!emailBranding || typeof emailBranding !== 'object') {
    // Return undefined if no branding, but could have templates
    if (emailTemplates && typeof emailTemplates === 'object') {
      return { emailTemplates: emailTemplates as Record<string, EmailTemplateOverride> };
    }
    return undefined;
  }

  return {
    ...(emailBranding as EmailBranding),
    emailTemplates: emailTemplates as Record<string, EmailTemplateOverride> | undefined,
  };
}
